import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { BackofficeAuthApiService } from '../core/backoffice-auth-api.service';
import { BackofficeSessionService } from '../core/backoffice-session.service';

@Component({
  selector: 'app-backoffice-session-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="page">
      <h1>{{ t('bo.login.title') }}</h1>

      @if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      }

      <form (ngSubmit)="submit()" #form="ngForm" class="form">
        <label>{{ t('bo.login.email') }}
          <input type="email" name="email" required [(ngModel)]="email" />
        </label>
        <label>{{ t('bo.login.password') }}
          <input type="password" name="password" required [(ngModel)]="password" />
        </label>
        <button type="submit" [disabled]="loading() || !form.valid">
          {{ loading() ? t('bo.login.loading') : t('bo.login.submit') }}
        </button>
      </form>

      @if (session.isAuthenticated()) {
        <p class="state state--success">{{ t('bo.login.active') }}</p>
        <p><a [routerLink]="['/backoffice/score-reports']">{{ t('bo.login.openUi') }}</a></p>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 520px; }
    h1 { margin: 0 0 0.5rem; font-size: 1.35rem; }
    .form { display: grid; gap: 0.7rem; }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; }
    input { padding: 0.55rem; border: 1px solid #c9d2dc; border-radius: 8px; }
    button { border: 0; border-radius: 8px; padding: 0.55rem 0.8rem; background: #1d4c9a; color: #fff; font-weight: 600; }
    .state--error { color: #9d1c1c; margin-top: 0.7rem; }
    .state--success { color: #1b6a31; margin-top: 0.7rem; }
    a { color: #1d4c9a; text-decoration: none; }
  `
})
export class BackofficeSessionPageComponent {
  private readonly api = inject(BackofficeAuthApiService);
  private readonly i18n = inject(I18nService);
  protected readonly session = inject(BackofficeSessionService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected email = '';
  protected password = '';
  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  constructor() {
    if (this.session.isAuthenticated()) {
      void this.router.navigateByUrl('/backoffice/score-reports');
    }
  }

  protected submit(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.api
      .login({
        email: this.email.trim(),
        password: this.password
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.session.setToken(response.token);
          this.session.setIdentity({
            id: response.user.id,
            email: response.user.email,
            roles: response.user.roles
          });
          this.loading.set(false);
          void this.router.navigateByUrl('/backoffice/score-reports');
        },
        error: (error) => {
          const apiMessage = error?.error?.error as string | undefined;
          this.errorMessage.set(this.humanizeBackofficeLoginError(apiMessage));
          this.loading.set(false);
        }
      });
  }

  private humanizeBackofficeLoginError(apiMessage?: string): string {
    if (!apiMessage) {
      return 'Connexion back-office impossible. Reessayez.';
    }
    if (apiMessage === 'Invalid credentials') {
      return 'Identifiants invalides.';
    }
    if (apiMessage === 'Account is not active') {
      return 'Compte back-office inactif.';
    }
    if (apiMessage === 'No back-office role assigned') {
      return 'Compte connecte mais sans role back-office actif.';
    }
    return apiMessage;
  }
}
